<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useBrowseNodeMaster } from '@/api/queries'
import type { BrowseNodeMasterRow } from '@/types/api'

const props = defineProps<{
  asin: string
  productName: string
  /** すでに割当済みのブラウズノードID(重複追加を視覚的に示す) */
  assignedIds: string[]
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'select', browseNodeId: string): void
}>()

// 入力中のフィルタ(確定はデバウンス)
const keywordInput = ref('')
const keyword = ref('')
let debounceTimer: ReturnType<typeof setTimeout> | undefined

watch(keywordInput, (v) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    keyword.value = v.trim()
  }, 350)
})

const enabled = ref(true)
const { data, isFetching } = useBrowseNodeMaster(keyword, enabled)

const items = computed(() => data.value?.items ?? [])
const truncated = computed(() => data.value?.truncated ?? false)

const selectedId = ref<string | null>(null)

function pick(row: BrowseNodeMasterRow) {
  selectedId.value = row.BrowseNodeId
}

function confirm() {
  if (selectedId.value) {
    emit('select', selectedId.value)
  }
}

function isAssigned(id: string) {
  return props.assignedIds.includes(id)
}
</script>

<template>
  <div class="modal-overlay" @click.self="emit('close')">
    <div class="modal bn-modal">
      <div class="modal-head">
        <div class="modal-title">ブラウズノードを追加</div>
        <button class="modal-close" @click="emit('close')">✕</button>
      </div>

      <div class="bn-target">
        <span class="chip-id">{{ asin }}</span>
        <span class="bn-target-name">{{ productName }}</span>
      </div>

      <div class="bn-filter">
        <svg class="icon icon-sm" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input
          v-model="keywordInput"
          class="bn-filter-input"
          type="text"
          placeholder="ブラウズノードID・階層名で絞り込み（正規表現可）"
          autofocus
        />
        <span v-if="isFetching" class="bn-loading"><span class="spinner spinner-sm"></span> 検索中…</span>
      </div>

      <div v-if="truncated" class="bn-truncated">
        件数が多いため一部のみ表示しています。フィルタで絞り込んでください。
      </div>

      <div class="bn-list">
        <div v-if="items.length === 0 && !isFetching" class="empty-state" style="padding:20px 0;">
          該当するブラウズノードがありません
        </div>
        <div
          v-for="row in items"
          :key="row.BrowseNodeId"
          class="bn-item"
          :class="{ selected: selectedId === row.BrowseNodeId, assigned: isAssigned(row.BrowseNodeId) }"
          @click="pick(row)"
          @dblclick="pick(row); confirm()"
        >
          <span class="bn-item-id chip-id">{{ row.BrowseNodeId }}</span>
          <span class="bn-item-route" :title="row.BrowseNodeRouteName ?? ''">
            {{ row.BrowseNodeRouteName || row.BrowseNodeName || '（名称なし）' }}
          </span>
          <span v-if="isAssigned(row.BrowseNodeId)" class="bn-item-assigned">割当済</span>
        </div>
      </div>

      <div class="modal-foot">
        <button class="btn btn-ghost" @click="emit('close')">キャンセル</button>
        <button class="btn btn-primary" :disabled="!selectedId" @click="confirm">追加する</button>
      </div>
    </div>
  </div>
</template>
